import {useCallback, useEffect, useRef, useState} from "react";
import { useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import usePreviousValue  from "beautiful-react-hooks/usePreviousValue";
import { useConfigControls, useConfigRotation, useConfigTrackOrbitControls } from "../../hooks/config";
import useEmitMouseMove from "../../hooks/useEmitMouseMove";
import { CalcOrbitControlsDistance } from "renderer/components/Stage";

const Controls = ({ zoomDirection, mousePan, mouseRotate, offOnInteraction, rotateEnabled, speed, mouseZoom = true  }) => {
	const previousZoomDirection = usePreviousValue(zoomDirection);
	const controlsRef = useRef(null);
	const [userStarted, setUserStarted] = useState(false);
	const trackOrbitControls = useConfigTrackOrbitControls();
	const camera = useThree((s) => s.camera);
	const { triggerMouseMoveEvent } = useEmitMouseMove();

	const onControlsStart = () => {
		if (offOnInteraction) {
			setUserStarted(true);
		}
	};

	useEffect(() => {
		let newZoom = null;
		const controls = controlsRef.current,
			camera = controlsRef.current.object;

		//TODO: OrbitControls used by Drei dont give access to the same mechanism of zoom in/out (dolly) when mouse wheel is used so there can be a jump when user uses mouse and then the zoom buttons  :(
		if (previousZoomDirection !== undefined && zoomDirection !== previousZoomDirection) {
			if (zoomDirection === 0) {
				//reset to initial zoom
				newZoom = 1;
			} else {
				const zoomScale = Math.pow(0.95, controls.zoomSpeed);

				newZoom = zoomDirection > previousZoomDirection ?
					Math.max(controls.minZoom, Math.min(controls.maxZoom, camera.zoom / zoomScale)) :
					Math.max(controls.minZoom, Math.min(controls.maxZoom, camera.zoom * zoomScale));
			}
		}

		if (newZoom !== null) {
			camera.zoom = newZoom;
			camera.updateProjectionMatrix();
			controls.update();
		}
	}, [zoomDirection, previousZoomDirection]);

	const onCameraChange = useCallback(() => {
		if (trackOrbitControls) {
			const getCameraZoomVerticalHorizontal = camera.userData?.getCameraZoomVerticalHorizontal;
			const cameraTargetPositionV3 = camera.userData?.cameraTargetPositionV3;
			const modelRoot = camera.userData?.modelRoot;
			if (camera && cameraTargetPositionV3 && modelRoot) {
				const data = getCameraZoomVerticalHorizontal(camera, cameraTargetPositionV3, modelRoot);
				triggerMouseMoveEvent(data);
			}
		}
	}, [camera, trackOrbitControls]);

	const {maxDistance, minDistance} = CalcOrbitControlsDistance(camera.fov);

	return (
		<>
			<OrbitControls
				ref={controlsRef}
				makeDefault
				autoRotate={rotateEnabled && (!offOnInteraction || !userStarted)}
				autoRotateSpeed={speed}
				enablePan={mousePan}
				enableRotate={mouseRotate}
				enableZoom={mouseZoom}
				onStart={onControlsStart}
				zoomSpeed={1}
				maxZoom={2}
				minZoom={0.5}
				onEnd={onCameraChange}
				enableDamping={!trackOrbitControls}
				maxDistance={maxDistance}
				minDistance={minDistance}
			/>
		</>
	);
};

const ConfigControls = ({ zoomDirection }) => {
	const { pan, rotate, mouseZoom } = useConfigControls();
	const { enabled: rotateEnabled, offOnInteraction, speed } = useConfigRotation();

	return (
		<Controls
			zoomDirection={zoomDirection}
			mousePan={pan}
			mouseRotate={rotate}
			mouseZoom={mouseZoom}
			rotateEnabled={rotateEnabled}
			offOnInteraction={offOnInteraction}
			speed={speed}
		/>
	);
};

export default ConfigControls;

export {
	Controls,
};
